import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import RoleIcon from '../components/RoleIcon';
import styles from './EventDetail.module.css';

interface EventDetailType {
  id: number;
  title: string;
  start: string;
  end: string;
  description: string;
  eventImageUrl?: string;
  recurrenceType?: string;
  recurrenceInterval?: number;
  recurrenceEndDate?: string;
  leadingDepartmentId?: string;
  isLeadershipOnly?: boolean;
  relatedArticles?: {
    id: number;
    slug: string;
    title: string;
    excerpt?: string;
    category: string;
    skillLevel: string;
    viewCount: number;
    averageRating: number;
  }[];
  roles: {
    name: string;
    icon: string;
    capacity: number;
    department?: string;
  }[];
  rsvps: {
    eventId: number;
    username: string;
    attending: boolean;
    role: string;
    source: string;
    discordId?: string;
    avatarUrl?: string;
    userId?: number;
    displayName?: string;
    rank?: string;
  }[];
}

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [event, setEvent] = useState<EventDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [rsvpLoading, setRsvpLoading] = useState<string | null>(null);

  // Refresh user permissions on component mount to prevent stale cache issues
  useEffect(() => {
    refreshUser();
  }, []);

  useEffect(() => {
    if (!id) {
      setError('Invalid event ID');
      setLoading(false);
      return;
    }

    axios
      .get(`/Events/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        console.log('Event data received:', res.data);
        console.log('Event image URL:', res.data.eventImageUrl || res.data.EventImageUrl);
        console.log('IsLeadershipOnly (raw):', res.data.isLeadershipOnly, res.data.IsLeadershipOnly);
        // Normalize property names
        const eventData = {
          ...res.data,
          eventImageUrl: res.data.eventImageUrl || res.data.EventImageUrl,
          isLeadershipOnly: res.data.isLeadershipOnly || res.data.IsLeadershipOnly || false,
          rsvps: res.data.rsvps?.map((rsvp: any) => ({
            ...rsvp,
            attending: rsvp.attending !== undefined ? rsvp.attending : rsvp.Attending,
            role: rsvp.role || rsvp.Role,
            username: rsvp.username || rsvp.Username,
          })) || [],
        };
        console.log('Normalized isLeadershipOnly:', eventData.isLeadershipOnly);
        setEvent(eventData);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading event:', err);
        setError(err.response?.data?.message || 'Failed to load event details');
        setLoading(false);
      });
  }, [id, token]);

  const handleRSVP = async (roleName: string, action: 'join' | 'leave') => {
    if (!event || !user) return;

    setRsvpLoading(roleName);
    try {
      console.log('RSVP request:', { roleName, action, eventId: event.id });
      const rsvpResponse = await axios.post(
        `/Events/${event.id}/rsvp`,
        {
          roleName,
          action,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log('RSVP response:', rsvpResponse.data);

      // Refresh event data
      const response = await axios.get(`/Events/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Refreshed event data:', response.data);
      // Normalize property names
      const eventData = {
        ...response.data,
        eventImageUrl: response.data.eventImageUrl || response.data.EventImageUrl,
        isLeadershipOnly: response.data.isLeadershipOnly || response.data.IsLeadershipOnly || false,
        rsvps: response.data.rsvps?.map((rsvp: any) => ({
          ...rsvp,
          attending: rsvp.attending !== undefined ? rsvp.attending : rsvp.Attending,
          role: rsvp.role || rsvp.Role,
          username: rsvp.username || rsvp.Username,
        })) || [],
      };
      console.log('Normalized event data:', eventData);
      console.log('Normalized RSVPs:', eventData.rsvps);
      setEvent(eventData);
    } catch (err: any) {
      console.error('RSVP error:', err);
      console.error('Error details:', err.response?.data);
      alert(err.response?.data?.message || err.response?.data || 'Failed to update RSVP');
    } finally {
      setRsvpLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    
    let deleteAllOccurrences = false;
    
    // Check if this is a recurring event
    if (event.recurrenceType && event.recurrenceType !== 'None') {
      const userChoice = prompt(
        `This is a recurring event. Type "all" to delete all occurrences, or "one" to delete only this occurrence:`,
        'one'
      );
      
      if (!userChoice) return; // User cancelled
      
      if (userChoice.toLowerCase() === 'all') {
        deleteAllOccurrences = true;
        if (!window.confirm(`Are you sure you want to delete ALL occurrences of "${event.title}"?`)) return;
      } else {
        if (!window.confirm(`Are you sure you want to delete this occurrence of "${event.title}"?`)) return;
      }
    } else {
      // Non-recurring event
      if (!window.confirm(`Are you sure you want to delete "${event.title}"?`)) return;
    }

    try {
      await axios.delete(`/Events/${event.id}?deleteAllOccurrences=${deleteAllOccurrences}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert(deleteAllOccurrences ? 'All event occurrences deleted successfully' : 'Event deleted successfully');
      navigate('/events');
    } catch (err: any) {
      console.error('Delete error:', err);
      alert(err.response?.data?.message || 'Failed to delete event');
    }
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  const isUserRsvpedForRole = (roleName: string) => {
    if (!user || !event) return false;
    return event.rsvps.some(
      (rsvp) => rsvp.role === roleName && rsvp.attending && (rsvp.userId === user.userId || rsvp.username === user.username)
    );
  };

  const getUserRsvpedRole = () => {
    if (!user || !event) return null;
    const rsvp = event.rsvps.find(
      (rsvp) => rsvp.attending && (rsvp.userId === user.userId || rsvp.username === user.username)
    );
    return rsvp ? rsvp.role : null;
  };

  const getAttendeesForRole = (roleName: string) => {
    if (!event) return [];
    return event.rsvps.filter((rsvp) => rsvp.role === roleName && rsvp.attending);
  };

  const canEditEvent = () => {
    if (!user) return false;
    
    console.log('EventDetail - User object:', user);
    console.log('EventDetail - User role:', user.role);
    console.log('EventDetail - User flags:', user.flags);
    console.log('EventDetail - User scopes:', user.scopes);
    
    // Admin, Leader role, or users with CanEditEvents flag can edit/delete
    const canEdit = (
      user.role === 'Admin' ||
      user.role === 'Leader' ||
      user.flags?.includes('CanEditEvents')
    );
    
    console.log('EventDetail - Can edit:', canEdit);
    return canEdit;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading event details...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error || 'Event not found'}</div>
        <button className="button" onClick={() => navigate('/events')}>
          ← Back to Calendar
        </button>
      </div>
    );
  }

  const fullImageUrl = event.eventImageUrl?.startsWith('http') || event.eventImageUrl?.startsWith('data:')
    ? event.eventImageUrl
    : event.eventImageUrl
    ? `${process.env.REACT_APP_API_URL}${event.eventImageUrl}`
    : null;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate('/events')}>
            ← Back to Calendar
          </button>
          <h1 className={styles.title}>{event.title}</h1>
          {canEditEvent() && (
            <div className={styles.headerActions}>
              <button
                className={styles.editButton}
                onClick={() => navigate(`/events/${id}/edit`)}
              >
                ✏️ Edit
              </button>
              <button className={styles.deleteButton} onClick={handleDelete}>
                🗑️ Delete
              </button>
            </div>
          )}
        </div>

        {/* Event Image */}
        {fullImageUrl && (
          <div className={styles.imageContainer}>
            <img src={fullImageUrl} alt={event.title} className={styles.eventImage} />
          </div>
        )}

        {/* Leadership Only Badge */}
        {event.isLeadershipOnly && (
          <div className={styles.leadershipBadge}>
            <div className={styles.leadershipBadgeIcon}>🔒</div>
            <div className={styles.leadershipBadgeContent}>
              <div className={styles.leadershipBadgeTitle}>Leadership Only Event</div>
              <div className={styles.leadershipBadgeText}>
                This event is restricted to Tier 3 and Tier 4 position holders only.
              </div>
            </div>
          </div>
        )}

        {/* Event Details Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM7 11h5v5H7z"/>
            </svg>
            Event Details
          </h2>
          <div className={styles.detailGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Start:</span>
              <span className={styles.detailValue}>{formatDateTime(event.start)}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>End:</span>
              <span className={styles.detailValue}>{formatDateTime(event.end)}</span>
            </div>
            {event.leadingDepartmentId && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Leading Department:</span>
                <span className={styles.detailValue}>Department {event.leadingDepartmentId}</span>
              </div>
            )}
            {event.recurrenceType && event.recurrenceType !== 'none' && (
              <>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Recurrence:</span>
                  <span className={styles.detailValue}>
                    {event.recurrenceType.charAt(0).toUpperCase() + event.recurrenceType.slice(1)}
                    {event.recurrenceInterval && event.recurrenceInterval > 1
                      ? ` (every ${event.recurrenceInterval})`
                      : ''}
                  </span>
                </div>
                {event.recurrenceEndDate && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Recurrence Ends:</span>
                    <span className={styles.detailValue}>
                      {new Date(event.recurrenceEndDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Related Knowledge Base Articles Section */}
        {event.relatedArticles && event.relatedArticles.length > 0 && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
              Event Preparation - Knowledge Base Articles
            </h2>
            <div className={styles.articlesGrid}>
              {event.relatedArticles.map((article) => (
                <div
                  key={article.id}
                  className={styles.articleCard}
                  onClick={() => navigate(`/knowledge/${article.slug}`)}
                >
                  <h3 className={styles.articleTitle}>{article.title}</h3>
                  <div className={styles.articleMeta}>
                    <span className={styles.articleCategory}>{article.category}</span>
                    {article.skillLevel && (
                      <span className={styles.articleSkill}>• {article.skillLevel}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Description Section */}
        {event.description && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
              </svg>
              Description
            </h2>
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          </div>
        )}

        {/* RSVP Section */}
        {event.roles && event.roles.length > 0 && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
              RSVP & Attendees
            </h2>
            <div className={styles.rolesContainer}>
              {event.roles.map((role, index) => {
                const attendees = getAttendeesForRole(role.name);
                const isRsvped = isUserRsvpedForRole(role.name);
                const isFull = role.capacity > 0 && role.capacity !== 9999 && attendees.length >= role.capacity;
                const userRsvpedRole = getUserRsvpedRole();
                const isRsvpedToOtherRole = !!(userRsvpedRole && userRsvpedRole !== role.name);

                return (
                  <div key={`${role.name}-${index}`} className={styles.roleCard}>
                    <div className={styles.roleHeader}>
                      <div className={styles.roleInfo}>
                        <RoleIcon roleName={role.name} className={styles.roleIcon} />
                        <span className={styles.roleName}>{role.name}</span>
                      </div>
                      <div className={styles.roleCapacity}>
                        <span
                          className={
                            isFull ? styles.capacityFull : styles.capacityAvailable
                          }
                        >
                          {role.capacity === 0 || role.capacity === 9999 
                            ? `${attendees.length} / ∞` 
                            : `${attendees.length} / ${role.capacity}`
                          }
                        </span>
                      </div>
                    </div>

                    {/* Attendees List */}
                    {attendees.length > 0 && (
                      <div className={styles.attendeesList}>
                        {attendees.map((attendee, attIndex) => (
                          <div key={`${attendee.username}-${attIndex}`} className={styles.attendeeCard}>
                            <div className={styles.attendeeAvatar}>
                              {attendee.avatarUrl ? (
                                <img
                                  src={
                                    attendee.avatarUrl.startsWith('http')
                                      ? attendee.avatarUrl
                                      : `${process.env.REACT_APP_API_URL}${attendee.avatarUrl}`
                                  }
                                  alt={attendee.displayName || attendee.username}
                                  className={styles.avatarImage}
                                />
                              ) : (
                                <div className={styles.avatarPlaceholder}>
                                  {(attendee.displayName || attendee.username).charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className={styles.attendeeInfo}>
                              <span className={styles.attendeeName}>
                                {attendee.displayName || attendee.username}
                              </span>
                              {attendee.rank && (
                                <span className={styles.attendeeRank}>
                                  {attendee.rank}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* RSVP Button */}
                    {user && (
                      <div className={styles.rsvpActions}>
                        {isRsvped ? (
                          <button
                            className={styles.leaveButton}
                            onClick={() => handleRSVP(role.name, 'leave')}
                            disabled={rsvpLoading === role.name}
                          >
                            {rsvpLoading === role.name ? 'Updating...' : 'Leave'}
                          </button>
                        ) : (
                          <button
                            className={styles.joinButton}
                            onClick={() => handleRSVP(role.name, 'join')}
                            disabled={isFull || rsvpLoading === role.name || isRsvpedToOtherRole}
                            title={
                              isRsvpedToOtherRole
                                ? `You are already RSVP'd to ${userRsvpedRole}. Leave that role first.`
                                : isFull
                                ? 'This role is full'
                                : 'Join this role'
                            }
                          >
                            {rsvpLoading === role.name
                              ? 'Updating...'
                              : isFull
                              ? 'Full'
                              : isRsvpedToOtherRole
                              ? 'Already RSVP\'d'
                              : 'Join'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetail;
